import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { LucideAngularModule } from 'lucide-angular';
import { I18nService } from '../../../../core';
import { AuthService } from '../../../../core/services/auth.service';
import type { CompanyMembershipRole } from '../../../company-context/models/company.models';
import { ProjectsFacade } from '../../../projects/facades/projects.facade';
import { AppModalComponent, HexColorPickerPopoverComponent, ToastService } from '../../../../shared/ui';
import { PRIMARY_HEX_PALETTE, isLightHexColor } from '../../../../shared/utils/hex-color.util';
import {
  WORKFLOW_ACCENT_COLOR_PALETTE,
  WORKFLOW_FORM_ICON_KEYS,
} from '../../../../shared/utils/workflow-form-appearance.util';
import { workflowIconKeyToApi } from '../../../../shared/utils/workflow-icon-key.util';
import {
  parseCompanyUsersWithMembershipRoles,
  readProjectMemberUserId,
} from '../../../projects/utils/project-members-display.util';

type WorkflowMemberRole = 'Owner' | 'Editor' | 'Viewer';

interface WorkflowTemplateOption {
  id: string;
  title: string;
  description: string;
}

interface StageDraft {
  id: string;
  name: string;
  colorHex: string;
  stageType: 1 | 2 | 3;
  order: number;
}

interface MemberDraft {
  email: string;
  role: WorkflowMemberRole;
  companyMembershipRole?: CompanyMembershipRole;
}

interface ProjectMember {
  userId: string;
  name: string;
  email: string;
  role: WorkflowMemberRole;
  implicitCompanyAccess?: boolean;
  implicitCompanyRole?: 'Admin' | 'Owner';
}

interface CompanyUser {
  id: string;
  name: string;
  email: string;
}

const WORKFLOW_TEMPLATES: WorkflowTemplateOption[] = [
  { id: 'starter', title: 'Inicial', description: 'Fluxo simples para operação diária' },
  { id: 'marketing', title: 'Marketing', description: 'Planejamento, execução e revisão' },
  { id: 'project-management', title: 'Gestão de Projetos', description: 'Backlog, execução e entrega' },
  { id: 'product-engineering', title: 'Produto + Engenharia', description: 'Do discovery ao done' },
];

@Component({
  selector: 'app-create-workflow-from-project-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule, AppModalComponent, HexColorPickerPopoverComponent],
  template: `
    <app-modal
      [visible]="visible()"
      size="md"
      [showCloseButton]="false"
      [title]="t('workflows.list.createWorkflow')"
      [subtitle]="modalSubtitle()"
      (closed)="close()"
    >
      <div modalHeaderActions class="phase-inline">
        <span class="phase-inline-label">Fase {{ step() }} de 2</span>
        <div class="phase-inline-track">
          @for (phase of phases; track phase; let index = $index) {
            <span class="phase-inline-dot" [class.is-active]="step() === phase">{{ phase }}</span>
            @if (index < phases.length - 1) {
              <span class="phase-inline-line"></span>
            }
          }
        </div>
      </div>

      <div modalBody class="wizard-body" (click)="closeStageColorPicker()">
        @if (step() === 1) {
          <section class="wizard-section">
            @if (!projectId()) {
              <label class="field-label">
                {{ t('workflows.list.createModalSelectProject') }}
                <select
                  class="field-input field-select"
                  [ngModel]="selectedProjectIdForPicker()"
                  (ngModelChange)="selectedProjectIdForPicker.set($event)"
                >
                  <option value="">{{ t('workflows.list.createModalSelectProjectPlaceholder') }}</option>
                  @for (project of projectsForPicker(); track project.id) {
                    <option [value]="project.id">{{ project.name }}</option>
                  }
                </select>
              </label>
            }
            <h3 class="section-title">Template e configuração</h3>
            <div class="template-grid">
              @for (template of templates(); track template.id) {
                <button
                  type="button"
                  class="template-card"
                  [class.is-active]="selectedTemplate() === template.id"
                  (click)="selectedTemplate.set(template.id)"
                >
                  <strong>{{ template.title }}</strong>
                  <span>{{ template.description }}</span>
                </button>
              }
            </div>

            <h3 class="section-title">Configuração inicial</h3>
            <div class="workflow-form-grid">
              <label class="field-label">
                Nome do workflow
                <div class="icon-name-row">
                  <button
                    type="button"
                    class="icon-preview-btn"
                    [class.has-color]="!!workflowColorHex()"
                    [style.background]="workflowColorHex() || 'var(--bg-surface)'"
                    [style.borderColor]="workflowColorHex() || 'var(--border)'"
                    (click)="toggleWorkflowIconPicker()"
                  >
                    <lucide-icon [name]="workflowIconKey()" class="h-4 w-4 icon-preview-glyph"></lucide-icon>
                  </button>

                  <input
                    type="text"
                    class="field-input"
                    [ngModel]="workflowName()"
                    (ngModelChange)="workflowName.set($event)"
                    placeholder="Ex.: Backlog"
                  />

                  @if (isWorkflowIconPickerOpen()) {
                    <div class="icon-picker" (click)="$event.stopPropagation()">
                      <div class="icon-picker-head">
                        <strong>Ícone do workflow</strong>
                        <div class="picker-head-actions">
                          <button type="button" (click)="isWorkflowIconPickerOpen.set(false)">Fechar</button>
                        </div>
                      </div>

                      <div class="picker-actions">
                        <div class="picker-color-inline">
                          @for (color of colorOptions; track color) {
                            <button
                              type="button"
                              class="color-choice compact"
                              [style.background]="color"
                              [class.is-selected]="color === workflowColorHex()"
                              (click)="workflowColorHex.set(color)"
                              [title]="color"
                            >
                              <span class="sr-only">Selecionar cor {{ color }}</span>
                            </button>
                          }
                        </div>
                      </div>

                      <div class="icon-grid">
                        @for (icon of iconOptions; track icon) {
                          <button
                            type="button"
                            class="icon-choice"
                            [class.is-selected]="icon === workflowIconKey()"
                            (click)="selectWorkflowIcon(icon)"
                            [title]="icon"
                          >
                            <lucide-icon [name]="icon" class="h-4 w-4"></lucide-icon>
                          </button>
                        }
                      </div>
                    </div>
                  }
                </div>
              </label>
            </div>

            <label class="field-label">
              Descrição <span class="optional">(opcional)</span>
              <textarea
                class="field-textarea"
                rows="2"
                [ngModel]="workflowDescription()"
                (ngModelChange)="workflowDescription.set($event)"
              ></textarea>
            </label>

            <label class="field-label">
              Prazo padrão (dias) <span class="optional">(opcional)</span>
              <input
                type="number"
                class="field-input"
                min="0"
                step="1"
                [ngModel]="defaultDueInDays()"
                (ngModelChange)="onDefaultDueInDaysChange($event)"
                placeholder="Ex.: 3"
              />
              <span class="field-help">Null = sem prazo automático. 0 = hoje. > 0 = hoje + N dias.</span>
            </label>

            <label class="inline-checkbox">
              <input
                type="checkbox"
                [ngModel]="isAccessRestricted()"
                (ngModelChange)="onAccessRestrictedChange($event)"
              />
              <span>Restringir acesso ao workflow (membros do workflow + Admin/Owner)</span>
            </label>

            <h3 class="section-title">Status (stages)</h3>
            <div class="stages-list">
              @for (stage of stages(); track stage.id; let i = $index) {
                <article class="stage-row">
                  <div class="stage-order-cell">
                    <button
                      type="button"
                      class="stage-order stage-order-button"
                      [class.light-order]="isLightHexColor(stage.colorHex || stageColorFallback)"
                      [style.background]="stage.colorHex || stageColorFallback"
                      [title]="'Trocar cor da etapa ' + (i + 1)"
                      (click)="toggleStageColorPicker(stage.id, $event)"
                    >
                      {{ i + 1 }}
                    </button>
                    @if (openedStageColorPickerId() === stage.id) {
                      <app-hex-color-picker-popover
                        class="stage-color-picker-host"
                        [value]="stage.colorHex"
                        [fallbackHex]="stageColorFallback"
                        placement="after-host"
                        (valueCommit)="updateStageColor(stage.id, $event)"
                        (requestClose)="closeStageColorPicker()"
                      />
                    }
                  </div>
                  <input
                    type="text"
                    class="field-input stage-input"
                    [ngModel]="stage.name"
                    (ngModelChange)="updateStageName(stage.id, $event)"
                  />
                  <select
                    class="permission-select stage-type"
                    [ngModel]="stage.stageType"
                    (ngModelChange)="updateStageType(stage.id, $event)"
                  >
                    <option [ngValue]="1">Inicial</option>
                    <option [ngValue]="2">Processo</option>
                    <option [ngValue]="3">Final</option>
                  </select>
                  <button
                    type="button"
                    class="stage-icon-btn"
                    [disabled]="i === 0"
                    (click)="moveStage(i, -1)"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    class="stage-icon-btn"
                    [disabled]="i === stages().length - 1"
                    (click)="moveStage(i, 1)"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    class="stage-icon-btn danger"
                    [disabled]="stages().length <= 1"
                    (click)="removeStage(stage.id)"
                  >
                    ×
                  </button>
                </article>
              }
            </div>
            <div class="stage-add-row">
              <input
                type="text"
                class="field-input"
                [ngModel]="newStageName()"
                (ngModelChange)="newStageName.set($event)"
                placeholder="Nova etapa"
              />
              <button type="button" class="btn-secondary" (click)="addStage()">Adicionar</button>
            </div>
          </section>
        } @else {
          <section class="wizard-section">
            <h3 class="section-title">Membros do projeto</h3>
            <p class="inline-subtitle">Membros atuais do projeto base para este workflow.</p>
            @if (loadingProjectMembers()) {
              <p class="member-empty">Carregando membros do projeto...</p>
            }
            <div class="member-chip-list">
              @for (member of projectMembers(); track projectMemberTrack(member)) {
                <span class="member-chip">
                  {{ member.email }}
                  <span class="member-chip-role">{{ projectMemberDisplayRole(member) }}</span>
                </span>
              }
              @if (!projectMembers().length) {
                <p class="member-empty">Nenhum membro encontrado no projeto.</p>
              }
            </div>

            <h3 class="section-title">Membros extras do workflow</h3>
            <p class="inline-subtitle">
              Adicione pessoas somente para este workflow.
            </p>
            <div class="member-input-row">
              <input
                type="email"
                class="field-input"
                [ngModel]="memberEmailInput()"
                (ngModelChange)="memberEmailInput.set($event)"
                placeholder="email@empresa.com"
                (keyup.enter)="addMember()"
              />
              <select
                class="permission-select member-role-select"
                [ngModel]="memberRoleInput()"
                (ngModelChange)="memberRoleInput.set($event)"
              >
                <option value="Owner">{{ roleLabel('Owner') }}</option>
                <option value="Editor">{{ roleLabel('Editor') }}</option>
                <option value="Viewer">{{ roleLabel('Viewer') }}</option>
              </select>
              <button type="button" class="btn-secondary" (click)="addMember()">
                Adicionar
              </button>
            </div>

            <div class="member-chip-list">
              @for (member of members(); track member.email) {
                <span class="member-chip">
                  {{ member.email }}
                  <span class="member-chip-role">{{ roleLabel(member.role) }}</span>
                  @if (canRemoveWorkflowExtraMember(member)) {
                    <button type="button" class="member-chip-remove" (click)="removeMember(member.email)">×</button>
                  }
                </span>
              }
              @if (!members().length) {
                <p class="member-empty">Nenhum membro adicionado.</p>
              }
            </div>

            <p class="phase-note">
              O template selecionado será aplicado e o workflow será criado ao finalizar.
            </p>
          </section>
        }

        @if (errorMessage(); as error) {
          <p class="error-message">{{ error }}</p>
        }
      </div>

      <div modalFooter class="wizard-footer">
        @if (step() === 2) {
          <button
            type="button"
            class="btn-secondary wizard-footer-back"
            (click)="step.set(1)"
          >
            Voltar
          </button>
        }
        <div class="wizard-footer-actions">
          <button type="button" class="btn-secondary" (click)="close()">Cancelar</button>
          @if (step() === 1) {
            <button
              type="button"
              class="btn-primary"
              [disabled]="!workflowName().trim() || loadingProjectMembers() || !canContinueStep1()"
              (click)="goToMembersStep()"
            >
              {{ loadingProjectMembers() ? 'Carregando membros...' : 'Continuar' }}
            </button>
          } @else {
            <button type="button" class="btn-primary" [disabled]="saving()" (click)="finalizeCreation()">
              {{ saving() ? 'Criando...' : 'Criar workflow' }}
            </button>
          }
        </div>
      </div>
    </app-modal>
  `,
  styles: [
    `
      .wizard-body {
        display: grid;
        gap: 14px;
      }

      .phase-inline {
        display: inline-flex;
        align-items: center;
        gap: 8px;
      }

      .phase-inline-label {
        font-size: 12px;
        font-weight: 600;
        color: var(--muted);
      }

      .phase-inline-track {
        display: inline-flex;
        align-items: center;
        gap: 5px;
      }

      .phase-inline-dot {
        width: 18px;
        height: 18px;
        border-radius: 999px;
        border: 1px solid var(--border);
        background: var(--bg-surface);
        color: var(--muted);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 600;
      }

      .phase-inline-dot.is-active {
        border-color: color-mix(in srgb, #111827 26%, var(--border));
        background: color-mix(in srgb, #111827 10%, var(--bg-surface));
        color: #111827;
      }

      .phase-inline-line {
        width: 16px;
        height: 1px;
        background: var(--border);
      }

      .wizard-section {
        display: grid;
        gap: 12px;
      }

      .section-title {
        margin: 0;
        font-size: 15px;
        font-weight: 700;
        color: var(--text);
      }

      .template-grid {
        display: grid;
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: 8px;
      }

      .template-card {
        text-align: left;
        border: 1px solid var(--border);
        border-radius: 10px;
        padding: 10px 12px;
        background: var(--bg-surface);
        display: grid;
        gap: 3px;
      }

      .template-card strong {
        font-size: 14px;
        color: var(--text);
      }

      .template-card span {
        font-size: 12px;
        color: var(--muted);
      }

      .template-card.is-active {
        border-color: color-mix(in srgb, #60a5fa 56%, var(--border));
        box-shadow: 0 0 0 1px color-mix(in srgb, #60a5fa 30%, transparent);
        background: color-mix(in srgb, #eff6ff 65%, var(--bg-surface));
      }

      .workflow-form-grid {
        display: grid;
        gap: 10px;
      }

      .icon-name-row {
        position: relative;
        display: grid;
        grid-template-columns: 44px minmax(0, 1fr);
        gap: 8px;
      }

      .icon-preview-btn {
        height: 42px;
        border-radius: 10px;
        border: 1px solid var(--border);
        color: #fff;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
      }

      .icon-preview-glyph {
        color: var(--text);
      }

      .icon-preview-btn.has-color .icon-preview-glyph {
        color: #fff;
      }

      .field-input,
      .field-textarea {
        width: 100%;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: var(--bg-surface);
        color: var(--text);
        font-size: 14px;
      }

      .field-input {
        height: 42px;
        padding: 0 12px;
      }

      .field-select {
        cursor: pointer;
      }

      .field-textarea {
        resize: vertical;
        min-height: 56px;
        padding: 10px 12px;
      }

      .field-label {
        display: grid;
        gap: 6px;
        font-size: 13px;
        font-weight: 600;
        color: var(--text);
      }

      .optional {
        color: var(--muted);
        font-weight: 500;
      }

      .field-help {
        font-size: 11px;
        font-weight: 500;
        color: var(--muted);
      }

      .inline-checkbox {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-size: 13px;
        color: var(--text);
      }

      .icon-picker {
        position: fixed;
        z-index: 70;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: min(460px, calc(100vw - 24px));
        max-height: calc(100vh - 180px);
        overflow: auto;
        border: 1px solid var(--border);
        border-radius: 12px;
        background: var(--bg-surface);
        box-shadow: 0 8px 18px rgba(15, 23, 42, 0.14);
        padding: 12px;
        display: grid;
        gap: 10px;
      }

      .icon-picker-head {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }

      .picker-head-actions {
        display: flex;
        align-items: center;
        gap: 8px;
      }

      .icon-picker-head strong {
        font-size: 12px;
      }

      .icon-picker-head button {
        border: 0;
        background: transparent;
        color: var(--muted);
        font-size: 11px;
        cursor: pointer;
      }

      .picker-actions {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
      }

      .picker-color-inline {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 7px;
      }

      .icon-grid {
        display: grid;
        grid-template-columns: repeat(10, minmax(0, 1fr));
        gap: 4px;
        max-height: 220px;
        overflow: auto;
        padding-right: 3px;
      }

      .icon-choice {
        height: 26px;
        border-radius: 5px;
        border: 1px solid transparent;
        background: transparent;
        color: #6b7280;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .icon-choice:hover {
        background: color-mix(in srgb, var(--bg-surface) 80%, #e5e7eb);
        border-color: color-mix(in srgb, #94a3b8 20%, var(--border));
        color: #4b5563;
      }

      .icon-choice.is-selected {
        color: #4338ca;
        border-color: color-mix(in srgb, #6366f1 58%, var(--border));
        background: color-mix(in srgb, #eef2ff 78%, var(--bg-surface));
      }

      .color-choice {
        width: 100%;
        aspect-ratio: 1 / 1;
        border-radius: 999px;
        border: 1px solid color-mix(in srgb, var(--border) 70%, #000);
      }

      .color-choice.compact {
        width: 16px;
        height: 16px;
      }

      .color-choice.is-selected {
        box-shadow: 0 0 0 2px var(--bg-surface), 0 0 0 4px color-mix(in srgb, #111827 22%, transparent);
      }

      .stages-list {
        display: grid;
        gap: 8px;
      }

      .stage-row {
        display: grid;
        grid-template-columns: 28px minmax(0, 1fr) 120px 30px 30px 30px;
        gap: 6px;
        align-items: center;
        position: relative;
      }

      .stage-order-cell {
        position: relative;
        z-index: 5;
        width: 24px;
        height: 24px;
      }

      .stage-order {
        width: 24px;
        height: 24px;
        border-radius: 999px;
        border: 1px solid color-mix(in srgb, #111827 12%, var(--border));
        color: #ffffff;
        font-size: 11px;
        font-weight: 700;
        display: inline-flex;
        align-items: center;
        justify-content: center;
      }

      .stage-order-button {
        cursor: pointer;
      }

      .permission-select {
        height: 42px;
        border: 1px solid var(--border);
        border-radius: 10px;
        padding: 0 10px;
        background: var(--bg-surface);
        color: var(--text);
        font-size: 14px;
      }

      .stage-input {
        min-width: 0;
      }

      .stage-type {
        min-width: 0;
      }

      .stage-color-picker-host {
        position: absolute;
        top: 50%;
        left: calc(100% + 8px);
        transform: translateY(-50%);
        z-index: 25;
      }

      .stage-order-button.light-order {
        color: #0f172a;
        border-color: color-mix(in srgb, #0f172a 18%, var(--border));
      }

      .stage-icon-btn {
        height: 30px;
        width: 30px;
        border-radius: 8px;
        border: 1px solid var(--border);
        background: var(--bg-surface);
        color: var(--muted);
        font-size: 12px;
      }

      .stage-icon-btn.danger {
        color: #dc2626;
      }

      .stage-icon-btn:disabled {
        opacity: 0.45;
      }

      .stage-add-row {
        display: grid;
        grid-template-columns: minmax(0, 1fr) auto;
        gap: 8px;
      }

      .member-input-row {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 140px auto;
        gap: 8px;
        align-items: center;
      }

      .member-role-select {
        min-width: 0;
      }

      .member-chip-list {
        min-height: 64px;
        border: 1px solid var(--border);
        border-radius: 10px;
        padding: 8px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;
      }

      .member-chip {
        height: 28px;
        padding: 0 8px;
        border-radius: 999px;
        border: 1px solid var(--border);
        background: var(--hover);
        color: var(--text);
        font-size: 12px;
        display: inline-flex;
        align-items: center;
        gap: 6px;
      }

      .member-chip-role {
        color: var(--muted);
      }

      .member-chip-remove {
        border: 0;
        background: transparent;
        color: var(--muted);
        font-size: 14px;
        line-height: 1;
      }

      .member-empty {
        margin: 0;
        font-size: 12px;
        color: var(--muted);
      }

      .inline-subtitle {
        margin: 0;
        font-size: 12px;
        color: var(--muted);
      }

      .phase-note {
        margin: 0;
        font-size: 12px;
        color: var(--muted);
      }

      .error-message {
        margin: 0;
        font-size: 12px;
        color: #dc2626;
      }

      .wizard-footer {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: flex-end;
      }

      .wizard-footer-actions {
        display: inline-flex;
        align-items: center;
        gap: 8px;
      }

      .wizard-footer-back {
        margin-right: auto;
      }

      .btn-primary,
      .btn-secondary {
        height: 36px;
        border-radius: 10px;
        border: 1px solid var(--border);
        padding: 0 14px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
      }

      .btn-secondary {
        background: var(--bg-surface);
        color: var(--text);
      }

      .btn-primary {
        border-color: #111827;
        background: #111827;
        color: #fff;
      }

      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        border: 0;
      }
    `,
  ],
})
export class CreateWorkflowFromProjectModalComponent {
  private readonly http = inject(HttpClient);
  private readonly toast = inject(ToastService);
  private readonly i18n = inject(I18nService);
  private readonly auth = inject(AuthService);
  private readonly projectsFacade = inject(ProjectsFacade);
  readonly t = this.i18n.t.bind(this.i18n);

  readonly visible = input<boolean>(false);
  readonly projectId = input<string | null>(null);
  readonly projectName = input<string>('');

  readonly closed = output<void>();
  readonly created = output<string>();

  readonly step = signal<1 | 2>(1);
  readonly phases = [1, 2] as const;
  readonly templates = signal<WorkflowTemplateOption[]>(WORKFLOW_TEMPLATES);
  readonly selectedTemplate = signal<string>('starter');
  readonly workflowName = signal('Backlog');
  readonly workflowDescription = signal('Workflow principal');
  readonly defaultDueInDays = signal<number | null>(null);
  readonly isAccessRestricted = signal(false);
  readonly workflowIconKey = signal<string>('columns-3');
  readonly workflowColorHex = signal<string>('#3B82F6');
  readonly stages = signal<StageDraft[]>(this.getDefaultStages('starter'));
  readonly newStageName = signal('');
  readonly memberEmailInput = signal('');
  readonly memberRoleInput = signal<WorkflowMemberRole>('Editor');
  readonly members = signal<MemberDraft[]>([]);
  readonly projectMembers = signal<ProjectMember[]>([]);
  private readonly companyUsersParsed = signal<
    Array<{ id: string; name: string; email: string; companyRole: CompanyMembershipRole }>
  >([]);
  readonly loadingProjectMembers = signal(false);
  readonly saving = signal(false);
  readonly errorMessage = signal<string | null>(null);
  readonly isWorkflowIconPickerOpen = signal(false);
  readonly openedStageColorPickerId = signal<string | null>(null);
  readonly selectedProjectIdForPicker = signal('');

  readonly projectsForPicker = computed(() =>
    [...this.projectsFacade.projects()].sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    )
  );

  readonly modalSubtitle = computed(() => {
    const routeProjectId = this.projectId();
    const routeName = this.projectName().trim();
    if (routeProjectId) {
      return routeName ? this.t('workflows.list.createModalSubtitleProject', { name: routeName }) : '';
    }
    const picked = this.selectedProjectIdForPicker().trim();
    if (picked) {
      const p = this.projectsFacade.getProjectById(picked);
      const name = p?.name?.trim();
      if (name) return this.t('workflows.list.createModalSubtitleProject', { name });
    }
    return this.t('workflows.list.createModalSubtitlePickProject');
  });

  readonly iconOptions = WORKFLOW_FORM_ICON_KEYS;
  readonly colorOptions = WORKFLOW_ACCENT_COLOR_PALETTE;
  /** Cor padrão de etapa (novas etapas + “limpar” no seletor). */
  readonly stageColorFallback = '#4F46E5';
  readonly isLightHexColor = isLightHexColor;

  constructor() {
    effect(() => {
      if (!this.visible()) {
        this.reset();
        return;
      }
      void this.loadTemplates();
      if (!this.projectId()) {
        this.projectsFacade.retry();
      }
    });

    effect(() => {
      const templateId = this.selectedTemplate();
      const presetKey = this.resolveTemplatePresetKey(templateId);
      this.workflowName.set(this.getDefaultWorkflowName(presetKey));
      this.stages.set(this.getDefaultStages(presetKey));
    });
  }

  close(): void {
    this.closed.emit();
  }

  toggleWorkflowIconPicker(): void {
    this.isWorkflowIconPickerOpen.update((value) => !value);
  }

  selectWorkflowIcon(icon: string): void {
    this.workflowIconKey.set(icon);
    this.isWorkflowIconPickerOpen.set(false);
  }

  addStage(): void {
    const stageName = this.newStageName().trim();
    if (!stageName) return;
    this.stages.update((items) => [
      ...items,
      {
        id: crypto.randomUUID(),
        name: stageName,
        colorHex: '#4F46E5',
        stageType: 2,
        order: items.length + 1,
      },
    ]);
    this.newStageName.set('');
  }

  removeStage(stageId: string): void {
    this.stages.update((items) =>
      items
        .filter((stage) => stage.id !== stageId)
        .map((stage, index) => ({ ...stage, order: index + 1 }))
    );
  }

  updateStageName(stageId: string, name: string): void {
    this.stages.update((items) => items.map((s) => (s.id === stageId ? { ...s, name } : s)));
  }

  updateStageType(stageId: string, stageType: 1 | 2 | 3): void {
    this.stages.update((items) =>
      items.map((s) => (s.id === stageId ? { ...s, stageType } : s))
    );
  }

  updateStageColor(stageId: string, colorHex: string): void {
    this.stages.update((items) =>
      items.map((s) => (s.id === stageId ? { ...s, colorHex } : s))
    );
  }

  toggleStageColorPicker(stageId: string, event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.openedStageColorPickerId.update((current) => (current === stageId ? null : stageId));
  }

  closeStageColorPicker(): void {
    this.openedStageColorPickerId.set(null);
  }

  moveStage(index: number, direction: -1 | 1): void {
    this.stages.update((items) => {
      const next = index + direction;
      if (index < 0 || index >= items.length || next < 0 || next >= items.length) return items;
      const copy = [...items];
      const [moved] = copy.splice(index, 1);
      copy.splice(next, 0, moved);
      return copy.map((stage, orderIndex) => ({ ...stage, order: orderIndex + 1 }));
    });
  }

  addMember(): void {
    const email = this.memberEmailInput().trim().toLowerCase();
    if (!email || !this.isValidEmail(email)) return;
    const role = this.memberRoleInput();
    const companyMembershipRole = this.lookupCompanyMembershipRole(email);
    this.members.update((items) =>
      items.some((member) => member.email === email)
        ? items
        : [...items, { email, role, companyMembershipRole }]
    );
    this.memberEmailInput.set('');
    this.memberRoleInput.set('Editor');
  }

  private lookupCompanyMembershipRole(email: string): CompanyMembershipRole | undefined {
    return this.companyUsersParsed().find((u) => u.email === email)?.companyRole;
  }

  canRemoveWorkflowExtraMember(m: MemberDraft): boolean {
    const cr = m.companyMembershipRole;
    if (cr !== 'Admin' && cr !== 'Owner') return true;
    const se = this.auth.currentUser()?.email?.trim().toLowerCase();
    return !!(se && m.email === se);
  }

  removeMember(email: string): void {
    const row = this.members().find((m) => m.email === email);
    if (row && !this.canRemoveWorkflowExtraMember(row)) return;
    this.members.update((items) => items.filter((member) => member.email !== email));
  }

  roleLabel(role: WorkflowMemberRole): string {
    if (role === 'Owner') return this.t('projects.members.roles.owner');
    if (role === 'Viewer') return this.t('projects.members.roles.viewer');
    return this.t('projects.members.roles.editor');
  }

  projectMemberTrack(m: ProjectMember): string {
    return (m.userId || '').trim() || m.email;
  }

  projectMemberDisplayRole(m: ProjectMember): string {
    return this.roleLabel(m.role);
  }

  onAccessRestrictedChange(value: boolean): void {
    this.isAccessRestricted.set(value === true);
  }

  canContinueStep1(): boolean {
    return !!this.resolveEffectiveProjectId();
  }

  async goToMembersStep(): Promise<void> {
    const projectId = this.resolveEffectiveProjectId();
    if (!projectId) {
      this.errorMessage.set(this.t('workflows.list.createModalInvalidProject'));
      return;
    }
    this.errorMessage.set(null);
    this.loadingProjectMembers.set(true);
    try {
      await this.loadProjectMembers(projectId);
      this.step.set(2);
    } finally {
      this.loadingProjectMembers.set(false);
    }
  }

  onDefaultDueInDaysChange(value: number | string | null): void {
    if (value === null || value === '') {
      this.defaultDueInDays.set(null);
      return;
    }
    const parsed = typeof value === 'number' ? value : Number(value);
    if (!Number.isFinite(parsed)) {
      this.defaultDueInDays.set(null);
      return;
    }
    this.defaultDueInDays.set(Math.trunc(parsed));
  }

  async finalizeCreation(): Promise<void> {
    const projectId = this.resolveEffectiveProjectId();
    if (!projectId) {
      this.errorMessage.set(this.t('workflows.list.createModalInvalidProject'));
      return;
    }

    const workflowName = this.workflowName().trim();
    if (!workflowName) {
      this.errorMessage.set('Informe o nome do workflow.');
      return;
    }

    const validStages = this.stages()
      .map((s, index) => ({ ...s, order: index + 1, name: s.name.trim() }))
      .filter((s) => !!s.name);
    if (!validStages.length) {
      this.errorMessage.set('Adicione ao menos uma etapa.');
      return;
    }

    const normalizedDefaultDueInDays = this.normalizeDefaultDueInDays();
    if (normalizedDefaultDueInDays === undefined) {
      this.errorMessage.set('Prazo padrão deve ser maior ou igual a zero.');
      return;
    }

    this.saving.set(true);
    this.errorMessage.set(null);
    try {
      const workflowResponse = await firstValueFrom(
        this.http.post<{ id?: string }>('/api/workflows', {
          projectId,
          name: workflowName,
          description: this.workflowDescription().trim() || null,
          iconKey: workflowIconKeyToApi(this.workflowIconKey()),
          colorHex: this.workflowColorHex(),
          isAccessRestricted: this.isAccessRestricted(),
          ...(normalizedDefaultDueInDays !== null ? { defaultDueInDays: normalizedDefaultDueInDays } : {}),
        })
      );
      const workflowId = workflowResponse?.id ?? '';
      if (!workflowId) throw new Error('Workflow sem id retornado.');

      for (const stage of validStages) {
        await firstValueFrom(
          this.http.post(`/api/workflows/${workflowId}/stages`, {
            workflowId,
            name: stage.name,
            colorHex: stage.colorHex || '#4F46E5',
            order: stage.order,
            stageType: stage.stageType,
          })
        );
      }

      await this.syncWorkflowMembers(workflowId);

      this.toast.success('Workflow criado com sucesso.');
      this.created.emit(workflowId);
    } catch {
      this.errorMessage.set('Não foi possível criar o workflow. Tente novamente.');
    } finally {
      this.saving.set(false);
    }
  }

  private reset(): void {
    this.step.set(1);
    this.selectedTemplate.set('starter');
    this.workflowName.set('Backlog');
    this.workflowDescription.set('Workflow principal');
    this.defaultDueInDays.set(null);
    this.isAccessRestricted.set(false);
    this.workflowIconKey.set('columns-3');
    this.workflowColorHex.set('#3B82F6');
    this.stages.set(this.getDefaultStages('starter'));
    this.newStageName.set('');
    this.memberEmailInput.set('');
    this.memberRoleInput.set('Editor');
    this.members.set([]);
    this.companyUsersParsed.set([]);
    this.projectMembers.set([]);
    this.loadingProjectMembers.set(false);
    this.errorMessage.set(null);
    this.saving.set(false);
    this.isWorkflowIconPickerOpen.set(false);
    this.openedStageColorPickerId.set(null);
    this.selectedProjectIdForPicker.set('');
  }

  private resolveEffectiveProjectId(): string | null {
    const fromRoute = this.projectId()?.trim() || null;
    if (fromRoute) return fromRoute;
    const picked = this.selectedProjectIdForPicker().trim();
    return picked || null;
  }

  private normalizeDefaultDueInDays(): number | null | undefined {
    const value = this.defaultDueInDays();
    if (value == null) return null;
    if (!Number.isFinite(value)) return null;
    const normalized = Math.trunc(value);
    if (normalized < 0) return undefined;
    return normalized;
  }

  private async loadTemplates(): Promise<void> {
    try {
      const response = await firstValueFrom(this.http.get<unknown>('/api/workflow-templates'));
      const root = this.asRecord(response);
      const source = this.asArray(root['items'] ?? response);
      const templates = source
        .map((item) => this.asRecord(item))
        .map((item) => {
          const id = this.readString(item['id']);
          const name = this.readString(item['name']);
          const description = this.readString(item['description']);
          return {
            id,
            title: this.translateTemplateName(name),
            description: this.translateTemplateDescription(name, description),
          };
        })
        .filter((item) => !!item.id && !!item.title);
      const sorted = templates.sort((a, b) => this.templateOrderIndex(a.title) - this.templateOrderIndex(b.title));
      const nextTemplates = sorted.length ? sorted : WORKFLOW_TEMPLATES;
      this.templates.set(nextTemplates);
      if (!nextTemplates.some((item) => item.id === this.selectedTemplate())) {
        this.selectedTemplate.set(nextTemplates[0]?.id ?? 'starter');
      }
    } catch {
      this.templates.set(WORKFLOW_TEMPLATES);
    }
  }

  private resolveTemplatePresetKey(templateId: string): string {
    const byId = (templateId || '').trim().toLowerCase();
    const selected = this.templates().find((item) => item.id === templateId);
    const byTitle = (selected?.title || '').trim().toLowerCase();

    const candidate = `${byId} ${byTitle}`;
    if (candidate.includes('marketing')) return 'marketing';
    if (
      candidate.includes('project management') ||
      candidate.includes('project-management') ||
      candidate.includes('gestão de projetos') ||
      candidate.includes('gestao de projetos')
    ) {
      return 'project-management';
    }
    if (
      candidate.includes('product + engineering') ||
      candidate.includes('product-engineering') ||
      candidate.includes('produto + engenharia') ||
      candidate.includes('produto-engenharia')
    ) {
      return 'product-engineering';
    }
    return 'starter';
  }

  private getDefaultWorkflowName(templateId: string): string {
    const normalized = (templateId || '').toLowerCase();
    if (normalized.includes('marketing')) return 'Planejamento de campanhas';
    if (normalized.includes('project')) return 'Backlog';
    if (normalized.includes('product')) return 'Roadmap';
    return 'Backlog';
  }

  private getDefaultStages(templateId: string): StageDraft[] {
    const normalized = (templateId || '').toLowerCase();
    let names = ['Backlog', 'Em andamento', 'Feito'];
    if (normalized.includes('marketing')) {
      names = ['Ideias', 'Planejado', 'Em execução', 'Revisão', 'Publicado'];
    } else if (normalized.includes('project')) {
      names = ['Backlog', 'Em andamento', 'Em revisão', 'Concluído'];
    } else if (normalized.includes('product')) {
      names = ['Backlog', 'Pronto para desenvolvimento', 'Em desenvolvimento', 'QA', 'Concluído'];
    }
    return names.map((name, index) => ({
      id: crypto.randomUUID(),
      name,
      colorHex: PRIMARY_HEX_PALETTE[index % PRIMARY_HEX_PALETTE.length],
      order: index + 1,
      stageType: index === 0 ? 1 : index === names.length - 1 ? 3 : 2,
    }));
  }

  private async syncWorkflowMembers(workflowId: string): Promise<void> {
    const desiredMembers = this.buildDesiredWorkflowMembers();
    if (!desiredMembers.length) return;
    let users = await this.loadCompanyUsers();
    for (const member of desiredMembers) {
      let user = users.find((item) => item.email.toLowerCase() === member.email.toLowerCase());
      if (!user) {
        await this.inviteUser(member.email);
        users = await this.loadCompanyUsers();
        user = users.find((item) => item.email.toLowerCase() === member.email.toLowerCase());
      }
      if (!user) continue;
      await this.upsertWorkflowMember(workflowId, user.id, member.role);
    }
  }

  private buildDesiredWorkflowMembers(): MemberDraft[] {
    const merged = new Map<string, MemberDraft>();
    for (const member of this.projectMembers()) {
      const email = member.email.trim().toLowerCase();
      if (!email) continue;
      merged.set(email, {
        email,
        role: this.mapProjectRoleToWorkflowRole(member.role),
      });
    }
    for (const member of this.members()) {
      const email = member.email.trim().toLowerCase();
      if (!email) continue;
      merged.set(email, {
        email,
        role: member.role,
      });
    }
    return [...merged.values()];
  }

  private mapProjectRoleToWorkflowRole(role: WorkflowMemberRole): WorkflowMemberRole {
    if (role === 'Owner') return 'Owner';
    if (role === 'Viewer') return 'Viewer';
    return 'Editor';
  }

  private async loadProjectMembers(projectId: string): Promise<void> {
    try {
      let parsed: ReturnType<typeof parseCompanyUsersWithMembershipRoles> = [];
      try {
        const usersRes = await firstValueFrom(this.http.get<unknown>('/users'));
        parsed = parseCompanyUsersWithMembershipRoles(usersRes);
      } catch {
        try {
          const usersRes = await firstValueFrom(this.http.get<unknown>('/api/users'));
          parsed = parseCompanyUsersWithMembershipRoles(usersRes);
        } catch {
          parsed = [];
        }
      }
      const response = await firstValueFrom(this.http.get<unknown>(`/api/projects/${projectId}/members`));
      const root = this.asRecord(response);
      const source = this.resolveProjectMembersSource(response, root);
      let members = source
        .map((item) => this.asRecord(item))
        .map((item) => this.mapProjectMember(item))
        .filter((item) => !!item.email);
      const byEmail = new Map(parsed.map((u) => [u.email.toLowerCase(), u.id]));
      members = members
        .map((m) => ({
          ...m,
          userId: (m.userId || byEmail.get(m.email.toLowerCase()) || '').trim(),
        }))
        .filter((m) => !!m.userId);
      const sorted = [...members].sort((a, b) =>
        (a.name || a.email).toLowerCase().localeCompare((b.name || b.email).toLowerCase(), undefined, {
          sensitivity: 'base',
        })
      );
      this.companyUsersParsed.set(parsed);
      this.projectMembers.set(sorted);
    } catch {
      this.companyUsersParsed.set([]);
      this.projectMembers.set([]);
    }
  }

  private resolveProjectMembersSource(raw: unknown, root: Record<string, unknown>): unknown[] {
    if (Array.isArray(raw)) return raw;
    const items = this.asArray(root['items']);
    if (items.length) return items;
    const members = this.asArray(root['members']);
    const users = this.asArray(root['users']);
    const entries = this.asArray(root['entries']);
    return [...members, ...users, ...entries];
  }

  private mapProjectMember(item: Record<string, unknown>): ProjectMember {
    const user = this.asRecord(item['user']);
    const userId = readProjectMemberUserId(item, user);
    return {
      userId,
      name:
        this.readString(item['name']) ||
        this.readString(item['fullName']) ||
        this.readString(user['name']),
      email: (
        this.readString(item['email']) ||
        this.readString(user['email'])
      ).toLowerCase(),
      role: this.readWorkflowRole(item['role'] || user['role']),
    };
  }

  private async loadCompanyUsers(): Promise<CompanyUser[]> {
    try {
      const response = await firstValueFrom(this.http.get<unknown>('/users'));
      const source = this.asRecord(response)['items'] ?? response;
      return this.asArray(source)
        .map((item) => this.asRecord(item))
        .map((item) => ({
          id: this.readString(item['id']),
          name: this.readString(item['name']),
          email: this.readString(item['email']).toLowerCase(),
        }))
        .filter((item) => !!item.id && !!item.email);
    } catch {
      return [];
    }
  }

  private async inviteUser(email: string): Promise<void> {
    try {
      await firstValueFrom(this.http.post('/users/invite', { email, role: 'Member' }));
    } catch {
      // noop
    }
  }

  private async upsertWorkflowMember(
    workflowId: string,
    userId: string,
    role: WorkflowMemberRole
  ): Promise<void> {
    try {
      await firstValueFrom(this.http.post(`/api/workflows/${workflowId}/members`, { userId, role }));
      return;
    } catch {
      // noop
    }
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  }

  private asArray(value: unknown): unknown[] {
    return Array.isArray(value) ? value : [];
  }

  private readString(value: unknown): string {
    return typeof value === 'string' ? value.trim() : '';
  }

  private isValidEmail(value: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
  }

  private readWorkflowRole(value: unknown): WorkflowMemberRole {
    if (value === 'Owner') return 'Owner';
    if (value === 'Viewer') return 'Viewer';
    return 'Editor';
  }

  private translateTemplateName(name: string): string {
    const normalized = name.trim().toLowerCase();
    if (normalized === 'starter' || normalized === 'inicial') return 'Inicial';
    if (normalized === 'marketing teams' || normalized === 'marketing') return 'Marketing';
    if (normalized === 'project management' || normalized === 'gestao de projetos') return 'Gestão de Projetos';
    if (normalized === 'product + engineering' || normalized === 'produto + engenharia') {
      return 'Produto + Engenharia';
    }
    return name;
  }

  private templateOrderIndex(title: string): number {
    const normalized = title.trim().toLowerCase();
    if (normalized === 'inicial') return 0;
    if (normalized === 'marketing') return 1;
    if (normalized === 'gestão de projetos' || normalized === 'gestao de projetos') return 2;
    if (normalized === 'produto + engenharia') return 3;
    return 99;
  }

  private translateTemplateDescription(name: string, fallback: string): string {
    const normalized = name.trim().toLowerCase();
    if (normalized.includes('starter') || normalized.includes('inicial')) {
      return this.t('workflows.templates.descriptions.starter');
    }
    if (normalized.includes('marketing')) {
      return this.t('workflows.templates.descriptions.marketing');
    }
    if (normalized.includes('project') || normalized.includes('gestao de projetos')) {
      return this.t('workflows.templates.descriptions.projectmanagement');
    }
    if (normalized.includes('product') || normalized.includes('produto')) {
      return this.t('workflows.templates.descriptions.productengineering');
    }
    return fallback || this.t('workflows.templates.descriptions.default');
  }
}

